// pages/StaffDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../api';

export default function StaffDashboard() {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // ✅ Dynamic Stats
  const [stats, setStats] = useState({
    todaySales: 0,
    todayBillCount: 0,
    lowStockCount: 0,
    expiringCount: 0,
    totalMedicines: 0,
    totalParties: 0,
    todayTransactions: 0
  });
  
  // ✅ Dynamic Recent Activity
  const [recentActivity, setRecentActivity] = useState([]);
  
  // ✅ Dynamic Alerts
  const [alerts, setAlerts] = useState([]);
  
  // ✅ Low Stock Items
  const [lowStockItems, setLowStockItems] = useState([]);
  
  // ✅ Expiring Soon Items
  const [expiringItems, setExpiringItems] = useState([]);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // ✅ Fetch ALL Dashboard Data
  const fetchDashboardData = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const activities = [];
      const alertsList = [];
      let statsData = {
        todaySales: 0,
        todayBillCount: 0,
        lowStockCount: 0,
        expiringCount: 0,
        totalMedicines: 0,
        totalParties: 0,
        todayTransactions: 0
      };

      // ✅ 1. Fetch Medicines & Calculate Low Stock
      if (hasPermission('medicines')) {
        try {
          const { data: medicines } = await API.get('/medicines');
          statsData.totalMedicines = medicines.length;
          
          // Calculate low stock (quantity <= minStock or <= 10)
          const lowStock = medicines.filter(m => {
            const minStock = m.minStock || 10;
            return m.quantity <= minStock && m.quantity > 0;
          });
          
          // Out of stock
          const outOfStock = medicines.filter(m => m.quantity <= 0);
          
          statsData.lowStockCount = lowStock.length + outOfStock.length;
          setLowStockItems(lowStock.slice(0, 5)); // Top 5 low stock
          
          // Add alerts for out of stock
          outOfStock.slice(0, 3).forEach(item => {
            alertsList.push({
              type: 'danger',
              icon: '🚨',
              message: `${item.name} is OUT OF STOCK!`,
              time: 'Urgent'
            });
          });
          
          // Add activities for low stock
          if (lowStock.length > 0) {
            activities.push({
              type: 'warning',
              icon: '📦',
              message: `${lowStock.length} items running low on stock`,
              time: 'Stock Alert',
              color: '#f59e0b'
            });
          }
        } catch (err) {
          console.error('Failed to fetch medicines:', err);
        }
      }

      // ✅ 2. Fetch Expiring Medicines
      if (hasPermission('expiry')) {
        try {
          const { data: expiring } = await API.get('/medicines/near-expiry');
          statsData.expiringCount = expiring.length;
          setExpiringItems(expiring.slice(0, 5)); // Top 5 expiring
          
          // Separate expired and expiring soon
          const now = new Date();
          const expired = expiring.filter(m => new Date(m.expiryDate) < now);
          const expiringSoon = expiring.filter(m => {
            const expDate = new Date(m.expiryDate);
            const daysLeft = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
            return daysLeft > 0 && daysLeft <= 30;
          });
          
          // Add alerts for expired
          if (expired.length > 0) {
            alertsList.push({
              type: 'danger',
              icon: '⚠️',
              message: `${expired.length} medicine(s) have EXPIRED!`,
              time: 'Critical'
            });
          }
          
          // Add activity for expiring
          if (expiringSoon.length > 0) {
            activities.push({
              type: 'warning',
              icon: '⏰',
              message: `${expiringSoon.length} items expiring within 30 days`,
              time: 'Expiry Alert',
              color: '#ef4444'
            });
          }
        } catch (err) {
          console.error('Failed to fetch expiring:', err);
        }
      }

      // ✅ 3. Fetch Today's Sales
      if (hasPermission('sales')) {
        try {
          const { data: sales } = await API.get('/sales');
          
          // Filter today's sales
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          const todaySales = sales.filter(sale => {
            const saleDate = new Date(sale.createdAt || sale.date);
            saleDate.setHours(0, 0, 0, 0);
            return saleDate.getTime() === today.getTime();
          });
          
          // Calculate total
          const totalAmount = todaySales.reduce((sum, sale) => {
            return sum + (sale.totalAmount || sale.amount || 0);
          }, 0);
          
          statsData.todaySales = totalAmount;
          
          // Add recent sales to activity
          todaySales.slice(0, 3).forEach(sale => {
            activities.push({
              type: 'success',
              icon: '💰',
              message: `Sale of Rs${(sale.totalAmount || sale.amount || 0).toLocaleString()}`,
              time: formatTimeAgo(sale.createdAt || sale.date),
              color: '#10b981'
            });
          });
        } catch (err) {
          console.error('Failed to fetch sales:', err);
        }
      }

      // ✅ 4. Fetch Today's Bills
      if (hasPermission('billing')) {
        try {
          const { data: bills } = await API.get('/bills');
          
          // Filter today's bills
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          const todayBills = bills.filter(bill => {
            const billDate = new Date(bill.createdAt || bill.date);
            billDate.setHours(0, 0, 0, 0);
            return billDate.getTime() === today.getTime();
          });
          
          statsData.todayBillCount = todayBills.length;
          
          // Add recent bills to activity
          todayBills.slice(0, 2).forEach(bill => {
            activities.push({
              type: 'info',
              icon: '🧾',
              message: `Bill #${bill.billNumber || bill._id.slice(-6)} - Rs${(bill.totalAmount || bill.grandTotal || 0).toLocaleString()}`,
              time: formatTimeAgo(bill.createdAt || bill.date),
              color: '#3b82f6'
            });
          });
        } catch (err) {
          console.error('Failed to fetch bills:', err);
        }
      }

      // ✅ 5. Fetch Transactions
      if (hasPermission('transactions')) {
        try {
          const { data: transactions } = await API.get('/transactions');
          
          // Filter today's transactions
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          const todayTx = transactions.filter(tx => {
            const txDate = new Date(tx.createdAt || tx.date);
            txDate.setHours(0, 0, 0, 0);
            return txDate.getTime() === today.getTime();
          });
          
          statsData.todayTransactions = todayTx.length;
          
          // Add recent transactions to activity
          todayTx.slice(0, 2).forEach(tx => {
            const typeIcon = tx.type === 'payment' ? '💳' : tx.type === 'purchase' ? '🛒' : '↩️';
            const typeColor = tx.type === 'payment' ? '#10b981' : tx.type === 'purchase' ? '#f59e0b' : '#8b5cf6';
            
            activities.push({
              type: tx.type,
              icon: typeIcon,
              message: `${tx.type.charAt(0).toUpperCase() + tx.type.slice(1)}: Rs${(tx.amount || 0).toLocaleString()}`,
              time: formatTimeAgo(tx.createdAt || tx.date),
              color: typeColor
            });
          });
        } catch (err) {
          console.error('Failed to fetch transactions:', err);
        }
      }

      // ✅ 6. Fetch Parties Count
      if (hasPermission('parties')) {
        try {
          const { data: parties } = await API.get('/parties');
          statsData.totalParties = parties.length;
        } catch (err) {
          console.error('Failed to fetch parties:', err);
        }
      }

      // Sort activities by recency (most recent first)
      activities.sort((a, b) => {
        // Put alerts first, then by time
        if (a.time === 'Stock Alert' || a.time === 'Expiry Alert') return -1;
        if (b.time === 'Stock Alert' || b.time === 'Expiry Alert') return 1;
        return 0;
      });

      setStats(statsData);
      setRecentActivity(activities.slice(0, 8)); // Show max 8 activities
      setAlerts(alertsList);

    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [hasPermission]);

  // Format time ago
  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Recently';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  // Initial fetch
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Auto refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboardData(true);
    }, 300000); // 5 minutes
    
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  // Get greeting based on time
  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  // Format time
  const formatTime = () => {
    return currentTime.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format date
  const formatDate = () => {
    return currentTime.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Quick access features with permissions
  const allFeatures = [
    {
      key: 'medicines',
      title: 'Inventory',
      description: 'Manage medicines & stock',
      icon: '💊',
      path: '/medicines',
      color: '#3b82f6',
      bgGradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
      stats: `${stats.totalMedicines} items`
    },
    {
      key: 'billing',
      title: 'Billing',
      description: 'Create new bills',
      icon: '🧾',
      path: '/billing',
      color: '#10b981',
      bgGradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      stats: `${stats.todayBillCount} today`
    },
    {
      key: 'sales',
      title: 'Sales',
      description: 'View sales reports',
      icon: '📈',
      path: '/sales',
      color: '#f59e0b',
      bgGradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      stats: `Rs${stats.todaySales.toLocaleString()}`
    },
    {
      key: 'expiry',
      title: 'Expiry Tracker',
      description: 'Check expiring medicines',
      icon: '⏰',
      path: '/expiry',
      color: '#ef4444',
      bgGradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      stats: `${stats.expiringCount} alerts`,
      urgent: stats.expiringCount > 0
    },
    {
      key: 'parties',
      title: 'Parties',
      description: 'Suppliers & customers',
      icon: '🤝',
      path: '/parties',
      color: '#8b5cf6',
      bgGradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      stats: `${stats.totalParties} parties`
    },
    {
      key: 'transactions',
      title: 'Transactions',
      description: 'View all transactions',
      icon: '💳',
      path: '/transactions',
      color: '#06b6d4',
      bgGradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      stats: `${stats.todayTransactions} today`
    },
    {
      key: 'contact',
      title: 'Contact',
      description: 'Get support',
      icon: '📞',
      path: '/contact',
      color: '#ec4899',
      bgGradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
      stats: 'Support'
    },
    {
      key: 'profile',
      title: 'My Profile',
      description: 'View your profile',
      icon: '👤',
      path: '/profile',
      color: '#64748b',
      bgGradient: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
      stats: 'Settings'
    }
  ];

  // Filter features based on permissions
  const availableFeatures = allFeatures.filter(f => hasPermission(f.key));

  // Stat cards data
  const statCards = [
    {
      title: "Today's Sales",
      value: `Rs${stats.todaySales.toLocaleString()}`,
      icon: '💰',
      color: '#10b981',
      bgColor: '#ecfdf5',
      permission: 'sales',
      trend: stats.todaySales > 0 ? 'up' : null
    },
    {
      title: "Bills Generated",
      value: stats.todayBillCount,
      icon: '🧾',
      color: '#3b82f6',
      bgColor: '#eff6ff',
      permission: 'billing'
    },
    {
      title: "Low Stock Items",
      value: stats.lowStockCount,
      icon: '📦',
      color: '#f59e0b',
      bgColor: '#fffbeb',
      permission: 'medicines',
      warning: stats.lowStockCount > 0
    },
    {
      title: "Expiring Soon",
      value: stats.expiringCount,
      icon: '⚠️',
      color: '#ef4444',
      bgColor: '#fef2f2',
      permission: 'expiry',
      urgent: stats.expiringCount > 0
    }
  ];

  const visibleStats = statCards.filter(s => hasPermission(s.permission));

  return (
    <div style={styles.pageWrapper}>
      {/* Animated Background */}
      <div style={styles.bgGradient}></div>
      <div style={styles.bgPattern}></div>
      
      <div style={styles.container}>
        {/* Welcome Header */}
        <div style={styles.welcomeSection}>
          <div style={styles.welcomeLeft}>
            <div style={styles.greetingRow}>
              <span style={styles.waveEmoji}>👋</span>
              <div>
                <h1 style={styles.greeting}>{getGreeting()}, {user?.name?.split(' ')[0] || 'Staff'}!</h1>
                <p style={styles.welcomeSubtext}>
                  Welcome to <strong>{user?.pharmacyName || 'Pharmacy'}</strong>
                </p>
              </div>
            </div>
            
            <div style={styles.dateTimeCard}>
              <div style={styles.timeDisplay}>
                <span style={styles.clockIcon}>🕐</span>
                <span style={styles.time}>{formatTime()}</span>
              </div>
              <div style={styles.dateDisplay}>
                <span style={styles.calendarIcon}>📅</span>
                <span style={styles.date}>{formatDate()}</span>
              </div>
              
              {/* Refresh Button */}
              <button 
                onClick={() => fetchDashboardData(true)}
                disabled={refreshing}
                style={styles.refreshButton}
                title="Refresh Data"
              >
                <span style={{
                  display: 'inline-block',
                  animation: refreshing ? 'spin 1s linear infinite' : 'none'
                }}>
                  🔄
                </span>
              </button>
            </div>
          </div>
          
          <div style={styles.welcomeRight}>
            <div style={styles.staffCard}>
              <div style={styles.staffAvatar}>
                {user?.name?.charAt(0)?.toUpperCase() || 'S'}
              </div>
              <div style={styles.staffInfo}>
                <h3 style={styles.staffName}>{user?.name || 'Staff Member'}</h3>
                <div style={styles.staffIdBadge}>
                  <span style={styles.idIcon}>🆔</span>
                  <span style={styles.staffId}>{user?.staffId || 'STAFF-001'}</span>
                </div>
                <div style={styles.roleBadge}>
                  <span style={styles.roleIcon}>👥</span>
                  Staff Member
                </div>
              </div>
              <div style={styles.statusIndicator}>
                <span style={styles.statusDot}></span>
                Online
              </div>
            </div>
          </div>
        </div>

        {/* ✅ Critical Alerts Section */}
        {alerts.length > 0 && (
          <div style={styles.alertsSection}>
            {alerts.map((alert, index) => (
              <div key={index} style={{
                ...styles.alertCard,
                background: alert.type === 'danger' 
                  ? 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)'
                  : 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                borderColor: alert.type === 'danger' ? '#fecaca' : '#fcd34d'
              }}>
                <span style={styles.alertIcon}>{alert.icon}</span>
                <div style={styles.alertContent}>
                  <span style={styles.alertMessage}>{alert.message}</span>
                  <span style={styles.alertTime}>{alert.time}</span>
                </div>
                <button 
                  style={styles.alertAction}
                  onClick={() => navigate(alert.type === 'danger' && alert.message.includes('EXPIRED') ? '/expiry' : '/medicines')}
                >
                  View →
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Stats Overview */}
        {visibleStats.length > 0 && (
          <div style={styles.statsSection}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>
                <span>📊</span> Today's Overview
              </h2>
              <span style={styles.lastUpdated}>
                Last updated: {formatTime()}
              </span>
            </div>
            <div style={styles.statsGrid}>
              {loading ? (
                // Skeleton loading
                [1, 2, 3, 4].map(i => (
                  <div key={i} style={styles.statCardSkeleton}>
                    <div style={styles.skeletonIcon}></div>
                    <div style={styles.skeletonContent}>
                      <div style={styles.skeletonValue}></div>
                      <div style={styles.skeletonLabel}></div>
                    </div>
                  </div>
                ))
              ) : (
                visibleStats.map((stat, index) => (
                  <div 
                    key={stat.title}
                    style={{
                      ...styles.statCard,
                      animationDelay: `${index * 0.1}s`,
                      borderLeft: `4px solid ${stat.color}`
                    }}
                  >
                    <div style={{
                      ...styles.statIconWrapper,
                      background: stat.bgColor
                    }}>
                      <span style={styles.statIcon}>{stat.icon}</span>
                    </div>
                    <div style={styles.statContent}>
                      <div style={{
                        ...styles.statValue,
                        color: stat.urgent ? '#ef4444' : stat.warning ? '#f59e0b' : stat.color
                      }}>
                        {stat.value}
                        {(stat.urgent || stat.warning) && (
                          <span style={styles.alertBadge}>!</span>
                        )}
                        {stat.trend === 'up' && (
                          <span style={styles.trendUp}>↑</span>
                        )}
                      </div>
                      <div style={styles.statLabel}>{stat.title}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Quick Access */}
        <div style={styles.quickAccessSection}>
          <h2 style={styles.sectionTitle}>
            <span>⚡</span> Quick Access
          </h2>
          <p style={styles.sectionSubtitle}>Jump to your available features</p>
          
          {loading ? (
            <div style={styles.loadingGrid}>
              {[1, 2, 3, 4].map(i => (
                <div key={i} style={styles.skeletonCard}></div>
              ))}
            </div>
          ) : (
            <div style={styles.featuresGrid}>
              {availableFeatures.map((feature, index) => (
                <div
                  key={feature.key}
                  onClick={() => navigate(feature.path)}
                  style={{
                    ...styles.featureCard,
                    animationDelay: `${index * 0.05}s`
                  }}
                >
                  <div style={{
                    ...styles.featureIconWrapper,
                    background: feature.bgGradient
                  }}>
                    <span style={styles.featureIcon}>{feature.icon}</span>
                    {feature.urgent && (
                      <span style={styles.urgentDot}></span>
                    )}
                  </div>
                  <div style={styles.featureContent}>
                    <h3 style={styles.featureTitle}>{feature.title}</h3>
                    <p style={styles.featureDesc}>{feature.description}</p>
                    <span style={{
                      ...styles.featureStats,
                      color: feature.color
                    }}>
                      {feature.stats}
                    </span>
                  </div>
                  <div style={styles.featureArrow}>→</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom Section */}
        <div style={styles.bottomGrid}>
          {/* ✅ Recent Activity - Now Dynamic */}
          <div style={styles.activityCard}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>
                <span>📋</span> Recent Activity
              </h3>
              {recentActivity.length > 0 && (
                <span style={styles.activityCount}>{recentActivity.length} items</span>
              )}
            </div>
            
            {loading ? (
              <div style={styles.activityLoading}>
                {[1, 2, 3, 4].map(i => (
                  <div key={i} style={styles.activitySkeleton}>
                    <div style={styles.skeletonCircle}></div>
                    <div style={styles.skeletonLines}>
                      <div style={styles.skeletonLine}></div>
                      <div style={{...styles.skeletonLine, width: '60%'}}></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : recentActivity.length === 0 ? (
              <div style={styles.emptyActivity}>
                <span style={styles.emptyIcon}>📭</span>
                <p style={styles.emptyText}>No recent activity today</p>
                <span style={styles.emptyHint}>Activities will appear here as you work</span>
              </div>
            ) : (
              <div style={styles.activityList}>
                {recentActivity.map((activity, index) => (
                  <div key={index} style={styles.activityItem}>
                    <div style={{
                      ...styles.activityIcon,
                      background: activity.color ? `${activity.color}15` : '#f1f5f9'
                    }}>
                      {activity.icon}
                    </div>
                    <div style={styles.activityContent}>
                      <span style={styles.activityMessage}>{activity.message}</span>
                      <span style={styles.activityTime}>{activity.time}</span>
                    </div>
                    <div style={{
                      ...styles.activityDot,
                      background: activity.color || '#94a3b8'
                    }}></div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ✅ Low Stock Items - Dynamic */}
          {hasPermission('medicines') && (
            <div style={styles.lowStockCard}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>
                  <span>📦</span> Low Stock Alert
                </h3>
                {stats.lowStockCount > 0 && (
                  <span style={styles.lowStockBadge}>{stats.lowStockCount}</span>
                )}
              </div>
              
              {loading ? (
                <div style={styles.listLoading}>
                  {[1, 2, 3].map(i => (
                    <div key={i} style={styles.listSkeleton}></div>
                  ))}
                </div>
              ) : lowStockItems.length === 0 ? (
                <div style={styles.emptyList}>
                  <span style={styles.emptyListIcon}>✅</span>
                  <p>All items are well stocked!</p>
                </div>
              ) : (
                <div style={styles.lowStockList}>
                  {lowStockItems.map((item, index) => (
                    <div key={index} style={styles.lowStockItem}>
                      <div style={styles.itemInfo}>
                        <span style={styles.itemName}>{item.name}</span>
                        <span style={styles.itemBatch}>Batch: {item.batchNumber || 'N/A'}</span>
                      </div>
                      <div style={{
                        ...styles.itemQty,
                        color: item.quantity <= 5 ? '#ef4444' : '#f59e0b'
                      }}>
                        {item.quantity} left
                      </div>
                    </div>
                  ))}
                  {stats.lowStockCount > 5 && (
                    <button 
                      style={styles.viewAllBtn}
                      onClick={() => navigate('/medicines')}
                    >
                      View all {stats.lowStockCount} items →
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ✅ Expiring Soon - Dynamic */}
          {hasPermission('expiry') && (
            <div style={styles.expiryCard}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>
                  <span>⏰</span> Expiring Soon
                </h3>
                {stats.expiringCount > 0 && (
                  <span style={styles.expiryBadge}>{stats.expiringCount}</span>
                )}
              </div>
              
              {loading ? (
                <div style={styles.listLoading}>
                  {[1, 2, 3].map(i => (
                    <div key={i} style={styles.listSkeleton}></div>
                  ))}
                </div>
              ) : expiringItems.length === 0 ? (
                <div style={styles.emptyList}>
                  <span style={styles.emptyListIcon}>🎉</span>
                  <p>No items expiring soon!</p>
                </div>
              ) : (
                <div style={styles.expiryList}>
                  {expiringItems.map((item, index) => {
                    const expDate = new Date(item.expiryDate);
                    const now = new Date();
                    const daysLeft = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
                    const isExpired = daysLeft <= 0;
                    
                    return (
                      <div key={index} style={styles.expiryItem}>
                        <div style={styles.itemInfo}>
                          <span style={styles.itemName}>{item.name}</span>
                          <span style={styles.itemBatch}>
                            Exp: {expDate.toLocaleDateString()}
                          </span>
                        </div>
                        <div style={{
                          ...styles.expiryDays,
                          background: isExpired ? '#fee2e2' : daysLeft <= 7 ? '#fef3c7' : '#f1f5f9',
                          color: isExpired ? '#dc2626' : daysLeft <= 7 ? '#d97706' : '#64748b'
                        }}>
                          {isExpired ? 'EXPIRED' : `${daysLeft}d left`}
                        </div>
                      </div>
                    );
                  })}
                  {stats.expiringCount > 5 && (
                    <button 
                      style={styles.viewAllBtn}
                      onClick={() => navigate('/expiry')}
                    >
                      View all {stats.expiringCount} items →
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Permissions Overview */}
        <div style={styles.permissionsCard}>
          <div style={styles.permissionsHeader}>
            <h3 style={styles.permissionsTitle}>
              <span>🔐</span> Your Access Permissions
            </h3>
            <span style={styles.permissionCount}>
              {availableFeatures.length} of {allFeatures.length} features
            </span>
          </div>
          <div style={styles.permissionsList}>
            {allFeatures.map(feature => (
              <div 
                key={feature.key}
                style={{
                  ...styles.permissionBadge,
                  ...(hasPermission(feature.key) ? styles.permissionActive : styles.permissionInactive)
                }}
              >
                <span>{feature.icon}</span>
                <span>{feature.title}</span>
                {hasPermission(feature.key) ? (
                  <span style={styles.checkMark}>✓</span>
                ) : (
                  <span style={styles.lockMark}>🔒</span>
                )}
              </div>
            ))}
          </div>
          <p style={styles.permissionsNote}>
            💡 Need more access? Contact your pharmacy administrator.
          </p>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <p style={styles.footerText}>
            Logged in as <strong>{user?.name}</strong> • {user?.pharmacyName} • 
            <span style={styles.footerTime}> {formatTime()}</span>
          </p>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes wave {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(20deg); }
          75% { transform: rotate(-15deg); }
        }
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// ✅ STYLES
const styles = {
  pageWrapper: {
    minHeight: '100vh',
    background: '#f8fafc',
    position: 'relative',
    overflow: 'hidden',
  },
  bgGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '400px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
    backgroundSize: '200% 200%',
    animation: 'gradient 15s ease infinite',
    opacity: 0.08,
    zIndex: 0,
  },
  bgPattern: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '400px',
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23667eea' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    zIndex: 0,
  },
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '30px',
    position: 'relative',
    zIndex: 1,
  },

  // Welcome Section
  welcomeSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '30px',
    marginBottom: '30px',
    flexWrap: 'wrap',
  },
  welcomeLeft: {
    flex: 1,
    minWidth: '300px',
  },
  greetingRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '20px',
  },
  waveEmoji: {
    fontSize: '48px',
    animation: 'wave 2s ease-in-out infinite',
    display: 'inline-block',
  },
  greeting: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
    lineHeight: 1.2,
  },
  welcomeSubtext: {
    color: '#64748b',
    fontSize: '16px',
    margin: '8px 0 0',
  },
  dateTimeCard: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  timeDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'white',
    padding: '12px 20px',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
  },
  clockIcon: { fontSize: '20px' },
  time: { fontSize: '18px', fontWeight: '700', color: '#1e293b' },
  dateDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'white',
    padding: '12px 20px',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
  },
  calendarIcon: { fontSize: '20px' },
  date: { fontSize: '14px', color: '#64748b' },
  refreshButton: {
    background: 'white',
    border: 'none',
    padding: '12px 16px',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    cursor: 'pointer',
    fontSize: '18px',
    transition: 'all 0.2s',
  },
  welcomeRight: {},
  staffCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
    border: '1px solid rgba(102, 126, 234, 0.1)',
    position: 'relative',
  },
  staffAvatar: {
    width: '64px',
    height: '64px',
    borderRadius: '16px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '28px',
    fontWeight: '700',
    boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)',
  },
  staffInfo: { flex: 1 },
  staffName: { margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b' },
  staffIdBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: '#f1f5f9',
    padding: '4px 10px',
    borderRadius: '6px',
    marginTop: '8px',
  },
  idIcon: { fontSize: '12px' },
  staffId: { fontSize: '12px', fontFamily: 'ui-monospace, monospace', color: '#475569', fontWeight: '600' },
  roleBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: '#eff6ff',
    color: '#3b82f6',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    marginTop: '8px',
    marginLeft: '8px',
  },
  roleIcon: { fontSize: '12px' },
  statusIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    background: '#dcfce7',
    color: '#166534',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#22c55e',
    animation: 'pulse 2s infinite',
  },

  // Alerts Section
  alertsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '24px',
  },
  alertCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px 20px',
    borderRadius: '14px',
    border: '1px solid',
    animation: 'fadeInUp 0.5s ease',
  },
  alertIcon: {
    fontSize: '24px',
  },
  alertContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  alertMessage: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e293b',
  },
  alertTime: {
    fontSize: '12px',
    color: '#64748b',
  },
  alertAction: {
    background: 'rgba(0,0,0,0.05)',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },

  // Stats Section
  statsSection: {
    marginBottom: '30px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '20px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
  },
  lastUpdated: {
    fontSize: '12px',
    color: '#94a3b8',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '20px',
  },
  statCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
    animation: 'fadeInUp 0.5s ease forwards',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  },
  statCardSkeleton: {
    background: 'white',
    borderRadius: '16px',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  skeletonIcon: {
    width: '52px',
    height: '52px',
    borderRadius: '14px',
    background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
  },
  skeletonContent: {
    flex: 1,
  },
  skeletonValue: {
    height: '28px',
    width: '80px',
    borderRadius: '6px',
    background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
    marginBottom: '8px',
  },
  skeletonLabel: {
    height: '14px',
    width: '100px',
    borderRadius: '4px',
    background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
  },
  statIconWrapper: {
    width: '52px',
    height: '52px',
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statIcon: { fontSize: '24px' },
  statContent: { flex: 1 },
  statValue: {
    fontSize: '28px',
    fontWeight: '700',
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  alertBadge: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    background: '#ef4444',
    color: 'white',
    fontSize: '12px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: 'pulse 1s infinite',
  },
  trendUp: {
    color: '#22c55e',
    fontSize: '18px',
  },
  statLabel: {
    fontSize: '13px',
    color: '#64748b',
    marginTop: '4px',
  },

  // Quick Access Section
  quickAccessSection: {
    marginBottom: '30px',
  },
  sectionSubtitle: {
    color: '#64748b',
    fontSize: '14px',
    margin: '-12px 0 20px',
  },
  loadingGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '20px',
  },
  skeletonCard: {
    height: '120px',
    borderRadius: '16px',
    background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '20px',
  },
  featureCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    cursor: 'pointer',
    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
    border: '1px solid rgba(226, 232, 240, 0.8)',
    transition: 'all 0.3s ease',
    animation: 'fadeInUp 0.5s ease forwards',
  },
  featureIconWrapper: {
    width: '56px',
    height: '56px',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
  },
  featureIcon: { fontSize: '26px' },
  urgentDot: {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    background: '#ef4444',
    border: '2px solid white',
    animation: 'pulse 1s infinite',
  },
  featureContent: { flex: 1 },
  featureTitle: { margin: 0, fontSize: '16px', fontWeight: '700', color: '#1e293b' },
  featureDesc: { margin: '4px 0 8px', fontSize: '13px', color: '#64748b' },
  featureStats: { fontSize: '12px', fontWeight: '700' },
  featureArrow: { fontSize: '20px', color: '#cbd5e1', transition: 'all 0.3s ease' },

  // Bottom Grid
  bottomGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '20px',
    marginBottom: '30px',
  },
  
  // Activity Card
  activityCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    margin: 0,
    fontSize: '16px',
    fontWeight: '700',
    color: '#1e293b',
  },
  activityCount: {
    fontSize: '12px',
    color: '#64748b',
    background: '#f1f5f9',
    padding: '4px 10px',
    borderRadius: '12px',
  },
  activityLoading: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  activitySkeleton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  skeletonCircle: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
  },
  skeletonLines: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  skeletonLine: {
    height: '12px',
    borderRadius: '4px',
    background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
  },
  emptyActivity: {
    textAlign: 'center',
    padding: '40px 20px',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '12px',
    display: 'block',
  },
  emptyText: {
    margin: '0 0 4px',
    fontSize: '15px',
    fontWeight: '600',
    color: '#64748b',
  },
  emptyHint: {
    fontSize: '12px',
    color: '#94a3b8',
  },
  activityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  activityItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    background: '#f8fafc',
    borderRadius: '12px',
    transition: 'all 0.2s',
  },
  activityIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
  },
  activityContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  activityMessage: { fontSize: '13px', color: '#1e293b', fontWeight: '500' },
  activityTime: { fontSize: '11px', color: '#94a3b8' },
  activityDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },

  // Low Stock Card
  lowStockCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  },
  lowStockBadge: {
    background: '#fef3c7',
    color: '#d97706',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '700',
  },
  listLoading: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  listSkeleton: {
    height: '50px',
    borderRadius: '10px',
    background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
  },
  emptyList: {
    textAlign: 'center',
    padding: '30px 20px',
    color: '#64748b',
  },
  emptyListIcon: {
    fontSize: '36px',
    display: 'block',
    marginBottom: '8px',
  },
  lowStockList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  lowStockItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    background: '#fffbeb',
    borderRadius: '10px',
    border: '1px solid #fef3c7',
  },
  itemInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  itemName: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#1e293b',
  },
  itemBatch: {
    fontSize: '11px',
    color: '#94a3b8',
  },
  itemQty: {
    fontSize: '13px',
    fontWeight: '700',
    padding: '4px 10px',
    borderRadius: '6px',
    background: 'white',
  },
  viewAllBtn: {
    width: '100%',
    padding: '10px',
    background: '#f1f5f9',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#64748b',
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'all 0.2s',
  },

  // Expiry Card
  expiryCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  },
  expiryBadge: {
    background: '#fee2e2',
    color: '#dc2626',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '700',
  },
  expiryList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  expiryItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    background: '#fef2f2',
    borderRadius: '10px',
    border: '1px solid #fecaca',
  },
  expiryDays: {
    fontSize: '11px',
    fontWeight: '700',
    padding: '4px 10px',
    borderRadius: '6px',
  },

  // Permissions Card
  permissionsCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '24px',
    marginBottom: '30px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  },
  permissionsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  permissionsTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    margin: 0,
    fontSize: '16px',
    fontWeight: '700',
    color: '#1e293b',
  },
  permissionCount: {
    fontSize: '13px',
    color: '#64748b',
    background: '#f1f5f9',
    padding: '4px 12px',
    borderRadius: '20px',
  },
  permissionsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
  },
  permissionBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '500',
    border: '2px solid',
  },
  permissionActive: {
    background: '#dcfce7',
    borderColor: '#86efac',
    color: '#166534',
  },
  permissionInactive: {
    background: '#f1f5f9',
    borderColor: '#e2e8f0',
    color: '#94a3b8',
  },
  checkMark: { color: '#22c55e', fontWeight: '700' },
  lockMark: { fontSize: '12px', opacity: 0.6 },
  permissionsNote: {
    margin: '16px 0 0',
    fontSize: '12px',
    color: '#64748b',
    textAlign: 'center',
  },

  // Footer
  footer: {
    textAlign: 'center',
    padding: '20px 0',
    borderTop: '1px solid #e2e8f0',
  },
  footerText: {
    margin: 0,
    fontSize: '13px',
    color: '#94a3b8',
  },
  footerTime: {
    color: '#64748b',
  },
};

